import { useCallback, useEffect, useReducer, useRef, useState, useTransition } from "react";
import { Network } from "./pages/network";
import { Navbar } from "./util/navbar";
import { OnEventSetter, SingleJSXElement, Transition, TransitionState, transitionToPackage } from "../types";
import styles from "../css/dist/index.module.css";
import { getCurrUser, sendVerifEmail } from "../connections";
import { User } from "../../../lib";
import { SignIn } from "./pages/signin";
import { SignOut } from "./pages/signout";
import { Recommendations } from "./pages/recommendations";
import { Conversations } from "./pages/conversations";
import { NotFound } from "./pages/nf";
import { Posts } from "./pages/posts";
import { Upload } from "./pages/upload";
import { VerificationActor, Verify } from "./pages/verify";
import { Profile } from "./pages/profile";
import { SignUp } from "./pages/signup";
import { Animated } from "react-animated-css";
import { ErrorNotif } from "./util/errornotif";
import { Home } from "./pages/home";
import { PasswordResetter, ResetPasswordRequester } from "./pages/resetpassword";
import { AllUsers } from "./pages/allusers";

export const STANDARD_TIME_BETWEEN_HIDE_AND_SHOW_TRANSITION_STARTS_MS = 500; export const _STBHASTSMS = STANDARD_TIME_BETWEEN_HIDE_AND_SHOW_TRANSITION_STARTS_MS;

export const redirect = async (startTransition: React.TransitionStartFunction, toPath: string, hideTransition: Transition, showTransition: Transition, setTransitionState: OnEventSetter<TransitionState>, setPath: OnEventSetter<string> ) => {
  window.history.pushState({ animate: false }, "", toPath);
  
  await runTransition(startTransition, setTransitionState, hideTransition, "hide");
  startTransition(() => { setPath(toPath); });
  await runTransition(startTransition, setTransitionState, showTransition, "show");
};

export const setTransitionToStable = (startTransition: React.TransitionStartFunction, setTransitionState: OnEventSetter<TransitionState>, result: "show" | "hide") => {
  startTransition(() => {
    setTransitionState(`${styles[result]} ${styles.transition_instant}`);
  });
};

export const runTransition = async (startTransition: React.TransitionStartFunction, setTransitionState: OnEventSetter<TransitionState>, transition: Transition, result: "show" | "hide") => {

  return new Promise<void>((resolve) => {
    if (transition === "instant") {
      startTransition(() => {
        setTransitionState(`${styles[result]} ${styles.transition_instant}`);
      });
      resolve();
      return;
    }

    window.scrollTo({ left: 0, top: 0, behavior: "smooth" });

    startTransition(() => {
      setTransitionState(`${
        styles[result]
      } ${
        styles[`transition_${transition}`]
      } ${
        styles[transitionToPackage[transition]]
      }`);
    });

    setTimeout(() => {
      startTransition(() => {
        setTransitionState(`${styles[result]} ${styles.transition_instant}`);
      });
      resolve();
    }, _STBHASTSMS);
  });

};

const App = () => {
  const [, startTransition] = useTransition();

  const [theme, setTheme] = useReducer((_: string, newValue: string) => {
    document.getElementsByTagName("html")[0].setAttribute("data-theme", newValue);
    return newValue;
  }, "");

  const [path, setPath] = useState("");
  const setPathCallback = useCallback((newPath: string) => { setPath(newPath); }, []);

  const [transitionState, setTransitionState] = useState(`${styles.show} ${styles.transition_instant}`);
  const setTransitionStateCallback = useCallback((newTransitionState: string) => { setTransitionState(newTransitionState); }, []);

  const [currUser, setCurrUser] = useState<User | false | undefined>();
  const setCurrUserCallback = useCallback((newCurrUser: User | false | undefined) => { setCurrUser(newCurrUser) }, []);

  // includes tablets
  const [isMobile, updateIsMobile] = useReducer(() => {
    // credit: https://stackoverflow.com/questions/11381673/detecting-a-mobile-browser, michael-zaporozhets @ stack overflow, detectmobilebrowsers.com
    let check = false;
    // eslint-disable-next-line
    (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||(window as any).opera);  // any bc opera will be undefined if nonexistent and supposedly exists (once existed) in opera, cld do more research but not important rn
    return check;
  }, false);

  const navbarRef = useRef<HTMLDivElement>(null);

  const [ifNotFound, setIfNotFound] = useState<SingleJSXElement>(null);

  const mainContainerRef = useRef<HTMLDivElement>(null);

  //////////
  // INIT //
  //////////
  const CODE_404_HIDE_DURATION_MS = 1000; const _C404HDMS = CODE_404_HIDE_DURATION_MS;  // allow some loading time before 404 display
  useEffect(() => {
    console.log("Badavas Beta v0.9.5");

    if (window.location.pathname !== '/' && window.location.pathname[window.location.pathname.length - 1] === '/') {
      setPath(window.location.pathname.slice(0, window.location.pathname.length - 1));  // do negative ixs work?
    } else {
      setPath(window.location.pathname);
    }
    
    updateIsMobile();

    if (localStorage.theme) setTheme(localStorage.theme);
    else setTheme("dark");
    
    getCurrUser().then(setCurrUser);
  }, []);

  useEffect(() => {
    mainContainerRef.current?.scrollTo(0, 0);
  }, [path]);

  useEffect(() => {
    setIfNotFound(null);
    setTimeout(() => {
      setIfNotFound((!currUser) ? <SignIn path={path} startTransition={startTransition} setTransitionState={setTransitionStateCallback} setPath={setPathCallback} setCurrUser={setCurrUserCallback}></SignIn> : <NotFound></NotFound>);
    }, _C404HDMS);
  }, [_C404HDMS, startTransition, setTransitionStateCallback, setPathCallback, setCurrUserCallback, currUser, path]);


  const handlePopState = useCallback((evt: PopStateEvent) => {
    redirect(startTransition, window.location.pathname, "maskRL", "maskRL", setTransitionState, setPath);
  }, []);
  useEffect(() => {
    window.addEventListener("popstate", handlePopState);

    return (() => {
      window.removeEventListener("popstate", handlePopState);
    });
  }, [handlePopState]);


  const Y_SCROLL_FORBIDDEN = new Set(["network", "chat", "signup"]);
  const HEIGHT_UNSET_MAIN_CONT = new Set(["skibidi"]);


  const [verifNotifClosed, setVerifNotifClosed] = useState(false);
  const [lastVerifEmailSend, setLastVerifEmailSend] = useState(Date.now());
  const MIN_TIME_BETWEEN_VERIF_EMAIL_SENDS_MS = 60 * 1000; const _MTBVESMS = MIN_TIME_BETWEEN_VERIF_EMAIL_SENDS_MS;
  const [timeLeftForResendText, updateTimeLeftForResendText] = useReducer((state: number, action: "REDUCE" | "RESET") => {
    switch (action) {
      case "REDUCE":
        return state - 1;  // no break neccessary bc return will exit switch
      case "RESET":
        return Math.round(_MTBVESMS / 1000);
    }
  }, 0);

  const [errorText, setErrorText] = useState<string | undefined>();
  const attemptVerifEmailSend = useCallback(async () => {
    if (!currUser || currUser.verified || verifNotifClosed) return;
    if (Date.now() - lastVerifEmailSend <= _MTBVESMS) {
      setErrorText("Please wait at least 60 seconds between verification email resends.");
      return;
    }
    setLastVerifEmailSend(Date.now());

    const success = sendVerifEmail(currUser.uid);
    if (!success) {
      setErrorText("Something unexpected happened... please confirm that you set your email correctly, then reload and try again if you do not recieve the new email.");
    }
  }, [_MTBVESMS, currUser, lastVerifEmailSend, verifNotifClosed]);

  useEffect(() => {
    updateTimeLeftForResendText("RESET");
  }, [lastVerifEmailSend]);
  useEffect(() => {
    if (!timeLeftForResendText) return;
    setTimeout(() => {
      updateTimeLeftForResendText("REDUCE");
    }, 1000);
  }, [timeLeftForResendText]);


  const [medium, setMedium] = useState(false); 

  const onResize = useCallback(() => {
    setMedium(window.innerWidth >= 768);
  }, []);
  useEffect(() => {
    onResize();

    window.addEventListener("resize", onResize);

    return (() => {
      window.removeEventListener("resize", onResize);
    });
  }, [onResize]);


  const setThemeCallback = useCallback((newTheme: string) => {
    setTheme(newTheme);
  }, []);


  // return (<div className={`${styles["bg-center"]} ${styles["bg-contain"]} ${styles["bg-no-repeat"]}`} style={{ backgroundImage: `url(${require("../utility/icon.png")})` }}>
  return (<div className={`${styles["bg-primary"]}`}>
    <Navbar setTheme={setThemeCallback} theme={theme} links={
      (!currUser) ? ["home", "signIn", "signUp"] :
      ["network", "recommendations", "posts", "conversations", "upload", "verify", "allUsers", "myProfile", "signOut"]
    } startTransition={startTransition} setTransitionState={setTransitionStateCallback} setPath={setPathCallback} path={path} currUser={currUser} ref={navbarRef}></Navbar>

    {
      currUser && !currUser.verified && !(path.split("/").length === 4 && path.split("/")[1] === "verify")
      ? (<Animated animationIn="fadeInUp" animationOut="fadeOutDown" isVisible={!verifNotifClosed} className={`${styles["fixed"]} ${styles["bottom-0"]} ${styles["left-0"]} ${styles["z-50"]} ${styles["right-0"]} ${styles["h-min"]} `}>
          <div className={`${styles["bg-primary"]} ${styles["p-6"]} ${styles["text-primary-content"]} ${styles["flex"]} ${styles["flex-col"]} ${styles["gap-4"]} ${styles["items-center"]} ${styles["justify-center"]}`}>

            <p>Your email is not verified. Check the email we sent to {currUser.email}.</p>
            
            <button className={`${styles["btn"]} ${styles["btn-secondary"]} ${styles["!text-secondary-content"]} ${styles["w-full"]}${timeLeftForResendText ? ` ${styles["btn-disabled"]} ${styles["opacity-50"]}` : ``}`} onClick={() => { attemptVerifEmailSend(); }}>Resend email{timeLeftForResendText ? ` (${timeLeftForResendText})` : ""}</button>

            <button className={`${styles["btn"]} ${styles["btn-secondary"]} ${styles["w-full"]}`} onClick={() => { setVerifNotifClosed(true); }}>Close, for now</button>

          </div>
        </Animated>)
      : null
    }
    
    
    {/* not hiding x and y overflow together for future flexibility smallest optimization lolll (writing this comment reduces the efficiency sufficiently for the combination of the overflow hidden classes to have been worth, perhaps) */}
    <div ref={mainContainerRef} className={`${transitionState} ${styles["relative"]} ${styles[Y_SCROLL_FORBIDDEN.has(path.split("/")[1]) ? "overflow-y-hidden" : "overflow-y-scroll"]} ${styles["overflow-x-hidden"]} ${styles["bg-base-100"]}`} style={(navbarRef.current && !HEIGHT_UNSET_MAIN_CONT.has(path.split("/")[1])) ? { height: `calc(100svh - ${navbarRef.current.scrollHeight}px)` } : {}}>
      {
        (path.split("/")[1] === "network" && currUser) ? <Network currUser={currUser} startTransition={startTransition} setTransitionState={setTransitionStateCallback} setPath={setPathCallback} path={path} mainContainerRef={mainContainerRef} isMobile={isMobile}></Network> :
        (path === "/signout" && currUser) ? <SignOut startTransition={startTransition} setTransitionState={setTransitionStateCallback} setPath={setPathCallback} setCurrUser={setCurrUserCallback}></SignOut> :
        (path === "/recommendations" && currUser) ? <Recommendations medium={medium} startTransition={startTransition} setTransitionState={setTransitionStateCallback} setPath={setPathCallback} path={path} mainContainerRef={mainContainerRef}></Recommendations> :
        (path.split("/")[1] === "chat" && currUser) ? <Conversations currUser={currUser} startTransition={startTransition} setTransitionState={setTransitionStateCallback} setPath={setPathCallback} path={path} mainContainerRef={mainContainerRef}></Conversations> :
        (path.split("/")[1] === "posts" && currUser) ? <Posts currUser={currUser} startTransition={startTransition} setTransitionState={setTransitionStateCallback} setPath={setPathCallback} path={path}></Posts> :
        (path === "/upload" && currUser) ? <Upload currUser={currUser} startTransition={startTransition} setTransitionState={setTransitionStateCallback} setPath={setPathCallback} path={path}></Upload> :
        (path === "/verify" && currUser) ? <Verify currUser={currUser} startTransition={startTransition} setTransitionState={setTransitionStateCallback} setPath={setPathCallback} path={path}></Verify> :
        (path.split("/")[1] === "profile" && currUser) ? <Profile currUser={currUser} startTransition={startTransition} setTransitionState={setTransitionStateCallback} setPath={setPathCallback} path={path}></Profile> :
        (path === "/signup") ? <SignUp theme={theme} mainContainerRef={mainContainerRef} startTransition={startTransition} setTransitionState={setTransitionStateCallback} setPath={setPathCallback} path={path} setCurrUser={setCurrUserCallback} updateTimeLeftForResendText={updateTimeLeftForResendText}></SignUp> :
        (path === "/") ? <Home theme={theme} medium={medium} currUser={currUser} startTransition={startTransition} setTransitionState={setTransitionStateCallback} setPath={setPathCallback} path={path} mainContainerRef={mainContainerRef}></Home> :
        (path === "/res-pw") ? <ResetPasswordRequester currUser={currUser} startTransition={startTransition} setTransitionState={setTransitionStateCallback} setPath={setPathCallback} path={path} mainContainerRef={mainContainerRef}></ResetPasswordRequester> :
        (path.split("/")[1] === "rpw") ? <PasswordResetter currUser={currUser} startTransition={startTransition} setTransitionState={setTransitionStateCallback} setPath={setPathCallback} path={path} mainContainerRef={mainContainerRef}></PasswordResetter> :
        (path === "/all-users" && currUser) ? <AllUsers currUser={currUser} startTransition={startTransition} setTransitionState={setTransitionStateCallback} setPath={setPathCallback} path={path}></AllUsers> :
        (path.split("/")[1] === "verify" && currUser) ? <VerificationActor currUser={currUser} startTransition={startTransition} setTransitionState={setTransitionStateCallback} setPath={setPathCallback} path={path} mainContainerRef={mainContainerRef} setCurrUser={setCurrUserCallback}></VerificationActor> :
        (path === "/signin" && !currUser) ? <SignIn path={path} startTransition={startTransition} /** still specify sign in here to avoid delay that ifNotFound has */ setTransitionState={setTransitionStateCallback} setPath={setPathCallback} setCurrUser={setCurrUserCallback}></SignIn> : ifNotFound
      }
    </div>
    <ErrorNotif errorText={errorText} onClose={() => { setErrorText(undefined); }}></ErrorNotif>
  </div>);
};

export default App;
